import { Dayjs, dayjs } from '../../../utils';

export type Vehicle = {
	id: string;
	make: string;
	model: string;
	license: string;
	state: string;
	startDate: string | Date;
	endDate: string | Date | null;
	minStartDate?: string | Date | null;
	maxEndDate?: string | Date | null;
};
const toUtcDay = (date?: string | Date | null) => (date ? dayjs.utc(date).startOf('day') : null);

const isVehicleActiveOnDate = (vehicle: Vehicle, date: Dayjs) => {
	const start = toUtcDay(vehicle.startDate);
	const end = toUtcDay(vehicle.endDate);

	if (!start) return false;
	if (start.isAfter(date, 'day')) return false;
	if (!end) return true;

	return end.isSame(date, 'day') || end.isAfter(date, 'day');
};

const getActiveVehicleCountOnDate = (vehicles: Vehicle[], date: Dayjs) =>
	vehicles.filter((vehicle) => isVehicleActiveOnDate(vehicle, date)).length;

const getRelevantBoundaryDates = (vehicles: Vehicle[]) => {
	const today = dayjs.utc().startOf('day');

	const dates = vehicles.flatMap((vehicle) => {
		const start = toUtcDay(vehicle.startDate);
		const end = toUtcDay(vehicle.endDate);

		return [...(start ? [start] : []), ...(end ? [end.add(1, 'day').startOf('day')] : [])];
	});

	const unique = Array.from(new Set([today.valueOf(), ...dates.map((d) => d.valueOf())]));

	return unique.map((value) => dayjs.utc(value)).sort((a, b) => a.valueOf() - b.valueOf());
};

const getNextBlockingVehicleStartDate = ({
	vehicles,
	startDate,
	maxActiveVehicles,
}: {
	vehicles: Vehicle[];
	startDate: Dayjs;
	maxActiveVehicles: number;
}) => {
	const futureStarts = vehicles
		.map((vehicle) => toUtcDay(vehicle.startDate))
		.filter((date): date is Dayjs => Boolean(date))
		.filter((date) => date.isAfter(startDate, 'day'))
		.sort((a, b) => a.valueOf() - b.valueOf());

	for (const futureStart of futureStarts) {
		const activeCount = getActiveVehicleCountOnDate(vehicles, futureStart);

		if (activeCount >= maxActiveVehicles) {
			return futureStart;
		}
	}

	return null;
};

const getMaxConcurrentVehicleCount = (vehicles: Vehicle[]) => {
	const boundaryDates = getRelevantBoundaryDates(vehicles);

	if (!boundaryDates.length) {
		return 0;
	}

	let maxCount = 0;

	for (const date of boundaryDates) {
		const activeCount = getActiveVehicleCountOnDate(vehicles, date);
		if (activeCount > maxCount) {
			maxCount = activeCount;
		}
	}

	return maxCount;
};

const hasMinimumActiveVehicleCoverage = ({
	vehicles,
	minActiveVehicles,
	effectiveStartDate,
}: {
	vehicles: Vehicle[];
	minActiveVehicles: number;
	effectiveStartDate: string | Date;
}) => {
	const normalizedEffectiveStartDate = toUtcDay(effectiveStartDate);

	if (!normalizedEffectiveStartDate) {
		return true;
	}

	const boundaryDates = Array.from(
		new Set([
			normalizedEffectiveStartDate.valueOf(),
			...vehicles.flatMap((vehicle) => {
				const start = toUtcDay(vehicle.startDate) ?? normalizedEffectiveStartDate;
				const end = toUtcDay(vehicle.endDate);

				return [
					(start.isBefore(normalizedEffectiveStartDate, 'day')
						? normalizedEffectiveStartDate
						: start
					).valueOf(),
					...(end ? [end.add(1, 'day').startOf('day').valueOf()] : []),
				];
			}),
		]),
	)
		.map((value) => dayjs.utc(value))
		.sort((a, b) => a.valueOf() - b.valueOf());

	return boundaryDates.every((date) => {
		const activeVehicleCount = vehicles.filter((vehicle) => {
			const start = toUtcDay(vehicle.startDate) ?? normalizedEffectiveStartDate;
			const end = toUtcDay(vehicle.endDate);

			if (start.isAfter(date, 'day')) {
				return false;
			}

			return !end || end.isSame(date, 'day') || end.isAfter(date, 'day');
		}).length;

		return activeVehicleCount >= minActiveVehicles;
	});
};

const getEarliestAvailableVehicleStartDate = ({
	vehicles,
	maxActiveVehicles,
}: {
	vehicles: Vehicle[];
	maxActiveVehicles: number;
}) => {
	const today = dayjs.utc().startOf('day');

	if (!maxActiveVehicles) {
		return today;
	}

	const boundaryDates = getRelevantBoundaryDates(vehicles).filter(
		(date) => date.isSame(today, 'day') || date.isAfter(today, 'day'),
	);

	for (const candidateDate of boundaryDates) {
		const activeCount = getActiveVehicleCountOnDate(vehicles, candidateDate);

		if (activeCount < maxActiveVehicles) {
			return candidateDate;
		}
	}

	return null;
};
export const getVehicleLimitState = ({
	vehicles,
	maxActiveVehicles,
}: {
	vehicles: Vehicle[];
	maxActiveVehicles: number;
}) => {
	const today = dayjs.utc().startOf('day');

	if (!maxActiveVehicles) {
		return {
			canAddMoreVehicles: true,
			defaultNewVehicleStartDate: today,
			maxNewVehicleEndDate: null,
			reason: '',
		};
	}

	const maxConcurrentVehicleCount = getMaxConcurrentVehicleCount(vehicles);
	const isGrandfatheredOverLimit = maxConcurrentVehicleCount > maxActiveVehicles;

	if (isGrandfatheredOverLimit) {
		return {
			canAddMoreVehicles: false,
			defaultNewVehicleStartDate: today,
			maxNewVehicleEndDate: null,
			reason: `This parker already exceeds the facility limit of ${maxActiveVehicles} active vehicle${maxActiveVehicles === 1 ? '' : 's'}. No additional vehicles can be added.`,
		};
	}

	const earliestAvailableStartDate = getEarliestAvailableVehicleStartDate({
		vehicles,
		maxActiveVehicles,
	});

	if (!earliestAvailableStartDate) {
		return {
			canAddMoreVehicles: false,
			defaultNewVehicleStartDate: today,
			maxNewVehicleEndDate: null,
			reason: `This facility allows a maximum of ${maxActiveVehicles} active vehicle${maxActiveVehicles === 1 ? '' : 's'}, and no future opening is currently available.`,
		};
	}

	const nextBlockingVehicleStartDate = getNextBlockingVehicleStartDate({
		vehicles,
		startDate: earliestAvailableStartDate,
		maxActiveVehicles,
	});

	return {
		canAddMoreVehicles: true,
		defaultNewVehicleStartDate: earliestAvailableStartDate,
		maxNewVehicleEndDate: nextBlockingVehicleStartDate
			? nextBlockingVehicleStartDate.subtract(1, 'day').startOf('day')
			: null,
		reason: '',
	};
};

export const validateVehicleLimit = ({
	vehicles,
	maxActiveVehicles,
}: {
	vehicles: Vehicle[];
	maxActiveVehicles: number;
}) => {
	if (!maxActiveVehicles) {
		return { isValid: true, reason: '' };
	}

	const maxConcurrentVehicleCount = getMaxConcurrentVehicleCount(vehicles);

	if (maxConcurrentVehicleCount <= maxActiveVehicles) {
		return { isValid: true, reason: '' };
	}
	const isPlural = maxActiveVehicles !== 1;

	return {
		isValid: false,
		reason: `This facility allows a maximum of ${maxActiveVehicles} active vehicle${
			isPlural ? 's' : ''
		}. Please update the vehicle dates so no more than ${maxActiveVehicles} vehicle${
			isPlural ? 's are' : ' is'
		} active at the same time.`,
	};
};

export const validateMinimumVehicleLimit = ({
	vehicles,
	minActiveVehicles,
	effectiveStartDate,
}: {
	vehicles: Vehicle[];
	minActiveVehicles: number;
	effectiveStartDate: string | Date;
}) => {
	if (!minActiveVehicles) {
		return { isValid: true, reason: '' };
	}

	if (
		hasMinimumActiveVehicleCoverage({
			vehicles,
			minActiveVehicles,
			effectiveStartDate,
		})
	) {
		return { isValid: true, reason: '' };
	}

	return {
		isValid: false,
		reason: `This facility requires at least ${minActiveVehicles} active vehicle${
			minActiveVehicles === 1 ? '' : 's'
		} per parker.`,
	};
};
